import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, XCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import type { CourseNodeContent, QuestionContent } from "@/types/courses";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

interface QuestionStepProps {
  step: CourseNodeContent;
  questionNumber: number;
  onCorrect: (xp: number) => void;
  onContinue: () => void;
}

const QuestionStep: React.FC<QuestionStepProps> = ({
  step,
  questionNumber,
  onCorrect,
  onContinue,
}) => {
  const q = step.content as QuestionContent;
  const [selected, setSelected] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const isCorrect = selected === q.correctAnswer;

  const handleCheck = () => {
    if (selected === null) return;
    setRevealed(true);
    if (selected === q.correctAnswer) {
      onCorrect(step.xpReward);
    }
  };

  const choiceClass = (idx: number) => {
    const base =
      "flex items-start gap-3 rounded-xl border p-4 transition-all cursor-pointer text-left";
    if (!revealed) {
      if (idx === selected) return `${base} border-primary bg-primary/5`;
      return `${base} border-border bg-card hover:bg-muted/40`;
    }
    if (idx === q.correctAnswer)
      return `${base} border-2 border-dashed border-correct bg-correct/10`;
    if (idx === selected)
      return `${base} border-2 border-incorrect bg-incorrect/10`;
    return `${base} border-border opacity-60`;
  };

  return (
    <div className="rounded-2xl border bg-card p-6 space-y-5">
      <p className="text-sm text-muted-foreground">Quick check</p>
      <h3 className="text-lg font-semibold leading-snug">{q.prompt}</h3>

      <div className="space-y-3">
        {q.choices.map((choice, idx) => (
          <button
            key={idx}
            className={choiceClass(idx)}
            onClick={() => !revealed && setSelected(idx)}
            disabled={revealed}
          >
            <span className="text-muted-foreground font-medium shrink-0">
              {LETTERS[idx]}.
            </span>
            <span className="flex-1 text-left">{choice}</span>
            {revealed && idx === q.correctAnswer && (
              <CheckCircle2 className="text-correct shrink-0" size={20} />
            )}
            {revealed && idx === selected && idx !== q.correctAnswer && (
              <XCircle className="text-incorrect shrink-0" size={20} />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {revealed && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`rounded-2xl p-5 ${
              isCorrect
                ? "bg-correct/10"
                : "bg-incorrect/10"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {isCorrect ? (
                  <CheckCircle2 className="text-correct" size={18} />
                ) : (
                  <XCircle className="text-incorrect" size={18} />
                )}
                <span
                  className={`font-semibold ${
                    isCorrect ? "text-correct" : "text-incorrect"
                  }`}
                >
                  {isCorrect ? "Nice work" : "Almost there"}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {isCorrect
                  ? `+${step.xpReward} XP earned`
                  : "No XP this time"}
              </span>
            </div>
            <div
              className={`text-sm ${
                isCorrect
                  ? "text-correct"
                  : "text-incorrect"
              }`}
            >
              <span className="font-medium block mb-2">Explanation</span>
              <div className="text-base max-w-none [&>p]:mb-3 [&>p]:leading-relaxed [&>p>strong]:font-semibold">
                <ReactMarkdown>{q.explanation.replace(/\s*•\s*/g, '\n\n• ')}</ReactMarkdown>
              </div>
              {!isCorrect && (
                <span className="block mt-2 text-xs text-muted-foreground">
                  Take another look — you've got this.
                </span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!revealed ? (
        <Button
          className="w-full"
          disabled={selected === null}
          onClick={handleCheck}
        >
          Check Answer
        </Button>
      ) : (
        <Button className="w-full" onClick={onContinue}>
          Continue
        </Button>
      )}
    </div>
  );
};

export default QuestionStep;
